import html2canvas from 'html2canvas';
import { firstValueFrom } from 'rxjs';

import { AiChatAttachmentsService } from 'src/app/services/ai-chat-attachments.service';
import { WsToolDeclaration } from '../ws/agent-ws.types';
import { AgentTool, AgentToolResult } from './agent-tool.types';

const SCREENSHOT_MIME = 'image/jpeg';
const SCREENSHOT_QUALITY = 0.85;
const AI_CHAT_TAG = 'ai-chat';

/**
 * Инструмент снимка текущей страницы
 */
export class AgentPageScreenshotTool implements AgentTool {
    /** @inheritdoc */
    public readonly declaration: WsToolDeclaration = {
        toolName: 'capture_page_screenshot',
        description:
            'Делает снимок текущего содержимого страницы приложения (без виджета чата), ' +
            'загружает его как вложение и возвращает fileId. Используй fileId как attachment ' +
            'в следующих сообщениях, когда нужно "увидеть" интерфейс пользователя.',
        requestFormat: {},
        responseFormat: { fileId: 'string', name: 'string', mime: 'string', size: 'number' },
    };

    private readonly attachmentsService: AiChatAttachmentsService;

    public constructor(attachmentsService: AiChatAttachmentsService) {
        this.attachmentsService = attachmentsService;
    }

    /** @inheritdoc */
    public async execute(_args: Record<string, unknown>): Promise<AgentToolResult> {
        try {
            const canvas = await html2canvas(document.documentElement, {
                useCORS: true,
                allowTaint: false,
                logging: false,
                scale: 1,
                ignoreElements: (element: Element) =>
                    element.tagName?.toLowerCase() === AI_CHAT_TAG,
            });

            const file = await this.canvasToFile(canvas);
            const uploaded = await firstValueFrom(this.attachmentsService.upload(file));

            return {
                ok: true,
                payload: {
                    fileId: uploaded.fileId,
                    name: uploaded.name,
                    mime: uploaded.mime,
                    size: uploaded.size,
                },
            };
        } catch (error) {
            console.error('Failed to capture page screenshot:', error);
            return {
                ok: false,
                error: {
                    code: 'SCREENSHOT_FAILED',
                    message: error instanceof Error ? error.message : 'Failed to capture page screenshot',
                },
            };
        }
    }

    private canvasToFile(canvas: HTMLCanvasElement): Promise<File> {
        return new Promise((resolve, reject) => {
            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        reject(new Error('Failed to encode screenshot image'));
                        return;
                    }

                    const name = `page-screenshot-${Date.now()}.jpg`;
                    resolve(new File([blob], name, { type: SCREENSHOT_MIME }));
                },
                SCREENSHOT_MIME,
                SCREENSHOT_QUALITY,
            );
        });
    }
}
